import React, { createContext, useContext, useState, useEffect } from 'react';
import { textToSpeech } from '../utils/a11yHelpers';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  // Load saved preferences from localStorage
  const loadPreference = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(`a11y_${key}`);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };

  const [textSize, setTextSize] = useState(() => loadPreference('textSize', 'normal'));
  const [highContrast, setHighContrast] = useState(() => loadPreference('highContrast', false));
  const [keyboardNavHelper, setKeyboardNavHelper] = useState(() => loadPreference('keyboardNavHelper', false));
  const [screenReaderMode, setScreenReaderMode] = useState(() => loadPreference('screenReaderMode', false));

  // Save preferences to localStorage
  const savePreference = (key, value) => {
    try {
      localStorage.setItem(`a11y_${key}`, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to save accessibility preference:', error);
    }
  };

  // Apply text size to HTML element
  useEffect(() => {
    document.documentElement.setAttribute('data-text-size', textSize);
    savePreference('textSize', textSize);
  }, [textSize]);

  // Apply high contrast mode to HTML element
  useEffect(() => {
    if (highContrast) {
      document.documentElement.setAttribute('data-contrast', 'high');
    } else {
      document.documentElement.removeAttribute('data-contrast');
    }
    savePreference('highContrast', highContrast);
  }, [highContrast]);

  // Apply keyboard navigation helper
  useEffect(() => {
    if (keyboardNavHelper) {
      document.documentElement.setAttribute('data-keyboard-nav', 'enabled');
    } else {
      document.documentElement.removeAttribute('data-keyboard-nav');
    }
    savePreference('keyboardNavHelper', keyboardNavHelper);
  }, [keyboardNavHelper]);

  // Apply screen reader mode with TTS
  useEffect(() => {
    if (screenReaderMode) {
      document.documentElement.setAttribute('data-screen-reader', 'enabled');
      // Disable animations for better screen reader experience
      document.documentElement.style.setProperty('--transition-duration', '0ms');
      
      // Add hover/focus event listeners for text-to-speech
      const handleElementFocus = (e) => {
        const element = e.target;
        let textToRead = '';
        
        // Get text from aria-label, title, or text content
        if (element.getAttribute('aria-label')) {
          textToRead = element.getAttribute('aria-label');
        } else if (element.title) {
          textToRead = element.title;
        } else if (element.tagName === 'IMG' && element.alt) {
          textToRead = element.alt;
        } else if (element.textContent && element.textContent.trim().length < 200) {
          textToRead = element.textContent.trim();
        }
        
        if (textToRead && textToRead.length > 0) {
          // Small delay to avoid reading too fast
          setTimeout(() => {
            textToSpeech.speak(textToRead);
          }, 100);
        }
      };
      
      // Add listeners to all interactive elements
      const interactiveElements = document.querySelectorAll(
        'button, a, input, select, textarea, [role="button"], [tabindex]:not([tabindex="-1"])'
      );
      
      interactiveElements.forEach(el => {
        el.addEventListener('mouseenter', handleElementFocus);
        el.addEventListener('focus', handleElementFocus);
      });
      
      // Cleanup function
      return () => {
        interactiveElements.forEach(el => {
          el.removeEventListener('mouseenter', handleElementFocus);
          el.removeEventListener('focus', handleElementFocus);
        });
        textToSpeech.stop();
      };
    } else {
      document.documentElement.removeAttribute('data-screen-reader');
      document.documentElement.style.removeProperty('--transition-duration');
      textToSpeech.stop();
    }
    savePreference('screenReaderMode', screenReaderMode);
  }, [screenReaderMode]);

  // Reset all settings
  const resetAccessibility = () => {
    setTextSize('normal');
    setHighContrast(false);
    setKeyboardNavHelper(false);
    setScreenReaderMode(false);
    textToSpeech.stop();
    
    // Clear localStorage
    ['textSize', 'highContrast', 'keyboardNavHelper', 'screenReaderMode'].forEach(key => {
      localStorage.removeItem(`a11y_${key}`);
    });
  };

  const value = {
    // State
    textSize,
    highContrast,
    keyboardNavHelper,
    screenReaderMode,
    
    // Setters
    setTextSize,
    setHighContrast,
    setKeyboardNavHelper,
    setScreenReaderMode,
    
    // Actions
    resetAccessibility,
    
    // Text-to-Speech controls
    speakText: textToSpeech.speak,
    stopSpeaking: textToSpeech.stop,
    pauseSpeaking: textToSpeech.pause,
    resumeSpeaking: textToSpeech.resume,
    isSpeaking: textToSpeech.isSpeaking,
    
    // Helper to check if any accessibility feature is enabled
    isAccessibilityEnabled: () => {
      return textSize !== 'normal' || highContrast || keyboardNavHelper || screenReaderMode;
    }
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityContext;

